/**
 * Famílias — regras puras.
 *
 * A família é o agrupamento que o produto usa para três coisas: a visão
 * financeira familiar, a lista de compras da família e a hospedagem própria.
 * A modalidade `per_family` da divisão de despesa depende dela — e é por isso
 * que este módulo existe antes de qualquer refinamento financeiro.
 *
 * Dois invariantes, e os dois têm consequência visível:
 *
 * 1. **Uma família ativa por pessoa por viagem.** Não é preferência: se
 *    alguém pudesse estar em duas, a divisão por família contaria a pessoa
 *    duas vezes e a soma deixaria de fechar. O banco garante com índice
 *    parcial; estas funções garantem a mensagem certa antes de chegar lá.
 *
 * 2. **Entrar numa família exige aceite, e o convite expira em 7 dias.**
 *    Ser colocado num agrupamento que muda como suas despesas são divididas
 *    não pode acontecer sem você saber.
 */

/** Prazo do convite de família, aprovado com o roadmap. */
export const FAMILY_INVITE_TTL_DAYS = 7;

export type FamilyInvitationStatus = 'pending' | 'accepted' | 'declined' | 'revoked';

export interface FamilyInvitation {
  status: FamilyInvitationStatus;
  /** Instante de expiração, em ms. */
  expiresAt: number;
}

export type FamilyInviteRejection =
  | 'not_found'
  | 'expired'
  | 'revoked'
  | 'already_answered'
  | 'already_in_family';

export const FAMILY_INVITE_MESSAGES: Record<FamilyInviteRejection, string> = {
  not_found: 'Este convite de família não existe mais.',
  expired: `Este convite expirou. Convites de família valem ${FAMILY_INVITE_TTL_DAYS} dias.`,
  revoked: 'Este convite foi cancelado por quem convidou.',
  already_answered: 'Você já respondeu a este convite.',
  already_in_family:
    'Você já está em uma família nesta viagem. Saia da atual antes de entrar em outra.',
};

export type FamilyInviteCheck =
  | { valid: true }
  | { valid: false; reason: FamilyInviteRejection };

/**
 * O convite pode ser respondido agora?
 *
 * A ordem das checagens é a das ações possíveis: revogado e expirado pedem um
 * convite novo; "já respondeu" não pede nada; "já está numa família" pede sair
 * da atual. A mensagem mais acionável vem primeiro.
 */
export function checkFamilyInvitation(
  invitation: FamilyInvitation | null,
  options: { now?: number; alreadyInFamily?: boolean } = {},
): FamilyInviteCheck {
  const now = options.now ?? Date.now();

  if (!invitation) return { valid: false, reason: 'not_found' };
  if (invitation.status === 'revoked') return { valid: false, reason: 'revoked' };
  if (invitation.expiresAt <= now) return { valid: false, reason: 'expired' };
  if (invitation.status !== 'pending') return { valid: false, reason: 'already_answered' };
  if (options.alreadyInFamily) return { valid: false, reason: 'already_in_family' };

  return { valid: true };
}

/** Dias inteiros restantes, para a Central de Notificações mostrar o prazo. */
export function daysLeftOnInvite(expiresAt: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 86_400_000));
}

export function inviteDeadline(now: number = Date.now()): number {
  return now + FAMILY_INVITE_TTL_DAYS * 86_400_000;
}

/**
 * Cor da próxima família da viagem.
 *
 * A paleta chega por parâmetro em vez de ser importada: `@nv/core` são regras
 * de domínio e não deve depender do design system — o mesmo cálculo vale para
 * qualquer paleta.
 *
 * A paleta é uma LISTA, não um mapa fixo por família: uma viagem pode ter duas
 * famílias ou onze. Depois de esgotada, ela recomeça — duas famílias com a
 * mesma cor é ruim, e não ter cor é pior, porque a cor é o que identifica a
 * família no pin do mapa e no anel do avatar.
 */
export function nextFamilyColor(
  usadas: readonly string[],
  palette: readonly string[],
): string {
  if (palette.length === 0) throw new Error('Paleta de famílias vazia.');
  const livre = palette.find((cor) => !usadas.includes(cor));
  return livre ?? (palette[usadas.length % palette.length] as string);
}

export interface FamilyMemberRef {
  memberId: string;
  familyId?: string | undefined;
}

/**
 * A viagem tem famílias suficientes para a divisão por família?
 *
 * Espelha `canSplitPerFamily`, mas a partir da lista de membros da viagem —
 * é o que a tela usa para habilitar ou não a modalidade **antes** de alguém
 * escolher os participantes da despesa.
 */
export function tripHasFamilies(membros: readonly FamilyMemberRef[]): boolean {
  return membros.some((m) => Boolean(m.familyId));
}

/** Agrupa membros por família, para a tela de grupo. */
export function groupByFamily<T extends FamilyMemberRef>(
  membros: readonly T[],
): { familyId: string; members: T[] }[] {
  const grupos = new Map<string, T[]>();

  for (const m of membros) {
    if (!m.familyId) continue;
    const atual = grupos.get(m.familyId) ?? [];
    atual.push(m);
    grupos.set(m.familyId, atual);
  }

  return [...grupos.entries()].map(([familyId, members]) => ({ familyId, members }));
}

/** Quem ainda não tem família. A tela mostra separado, não escondido. */
export function withoutFamily<T extends FamilyMemberRef>(membros: readonly T[]): T[] {
  return membros.filter((m) => !m.familyId);
}
